import React, { useEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from '@mui/material';
import { useGetRepositoryByIdQuery } from '../services/repositoryEntries';
import {
	CHART_COUNT_METRIC_VALUE,
	isChartDateLikePropertyType,
	type ChartPresentationVariant,
} from '../chart/canvasChartData';
import { EntityType, type Repository } from '../types';
import ChartWhereFilterBuilder from './ChartWhereFilterBuilder';
import { useAppSelector } from '../hooks';
import { selectIsAuthenticated } from '../containers/authSlice';
import { selectAllPropertyDefinitions } from '../containers/contentSlice';
import { sortEntryByRank } from '../common/util';

export const METRIC_PROPERTY_DEFINITION_TYPES = [
	EntityType.PropertyDefinitionBelongsTo,
	EntityType.PropertyDefinitionBoolean,
	EntityType.PropertyDefinitionNumber,
];

export const X_AXIS_PROPERTY_DEFINITION_TYPES = [
	EntityType.PropertyDefinitionDate,
	EntityType.PropertyDefinitionDatetime,
	EntityType.PropertyDefinitionNumber,
];

export interface AddChartDialogProps {
	open: boolean;
	repositories: Repository[];
	onClose: () => void;
	elementSelectSlot?: React.ReactNode;
	onSubmit: (payload: {
		repositoryId: string;
		title: string;
		where?: string;
		includeArchived: boolean;
		metric: string;
		xAxis: string;
		xAxisTimeScale?: 'D' | 'W' | 'M' | null;
		variant?: ChartPresentationVariant;
		targetA?: number | null;
		targetB?: number | null;
	}) => void;
}

const parseOptionalChartTargetInput = (raw: string): number | null => {
	const t = raw.trim();
	if (t === '') {
		return null;
	}
	const n = Number(t);
	return Number.isFinite(n) ? n : null;
};

const AddChartDialog: React.FC<AddChartDialogProps> = ({ open, repositories, onClose, onSubmit, elementSelectSlot }) => {
	const [repositoryId, setRepositoryId] = useState('');
	const [title, setTitle] = useState('');
	const [where, setWhere] = useState('');
	const [includeArchived, setIncludeArchived] = useState(true);
	const [metricSlug, setMetricSlug] = useState('');
	const [xAxisSlug, setXAxisSlug] = useState('');
	const [xAxisTimeScale, setXAxisTimeScale] = useState<'D' | 'W' | 'M' | ''>('');
	const [variant, setVariant] = useState<ChartPresentationVariant>('line');
	const [targetAStr, setTargetAStr] = useState('');
	const [targetBStr, setTargetBStr] = useState('');
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const allPropertyDefinitions = useAppSelector(selectAllPropertyDefinitions);

	const { data: repositoryById, isFetching: isRepositoryFetching } = useGetRepositoryByIdQuery(
		repositoryId,
		{ skip: !open || !repositoryId || !isAuthenticated }
	);
	const localPropertyDefinitions = useMemo(() => {
		return allPropertyDefinitions.filter((def) => def.parentId === repositoryId).sort(sortEntryByRank);
	}, [allPropertyDefinitions, repositoryId]);
	const propertyDefinitionsSource = useMemo(
		() =>
			(isAuthenticated ? (repositoryById?.propertyDefinitions ?? []) : localPropertyDefinitions) as Array<{
				slug: string;
				label?: string;
				type: EntityType;
				required?: boolean;
				targetRepositoryId?: string;
			}>,
		[isAuthenticated, repositoryById?.propertyDefinitions, localPropertyDefinitions]
	);
	const propertyOptions = useMemo(
		() =>
			propertyDefinitionsSource
				.map((property) => ({
					slug: property.slug,
					label: property.label?.trim() ? property.label : property.slug,
					type: property.type,
					required: (property as { required?: boolean }).required === true,
					targetRepositoryId:
						property.type === EntityType.PropertyDefinitionBelongsTo
							? String(
								(property as { targetRepositoryId?: string }).targetRepositoryId ?? '',
							).trim()
							: '',
				})),
		[propertyDefinitionsSource]
	);
	const metricOptions = useMemo(
		() =>
			propertyOptions.filter((option) =>
				METRIC_PROPERTY_DEFINITION_TYPES.includes(option.type as EntityType)
			).sort(sortEntryByRank),
		[propertyOptions]
	);
	const xAxisOptions = useMemo(
		() =>
			propertyOptions.filter((option) =>
				X_AXIS_PROPERTY_DEFINITION_TYPES.includes(option.type as EntityType)
			).sort(sortEntryByRank),
		[propertyOptions]
	);

	const selectedXAxisType = useMemo(
		() => xAxisOptions.find((p) => p.slug === xAxisSlug)?.type,
		[xAxisOptions, xAxisSlug]
	);
	const showTimeScale = isChartDateLikePropertyType(selectedXAxisType);
	const showTimeScaleControl = showTimeScale && (variant === 'line' || variant === 'bar');
	const showHeatmapVariant = showTimeScale;
	const showLineBarTargets =
		(variant === 'line' || variant === 'bar') && Boolean(xAxisSlug.trim());
	const presentationVariantOptions = useMemo(() => {
		const base: { value: ChartPresentationVariant; label: string }[] = [
			{ value: 'line', label: 'Line' },
			{ value: 'bar', label: 'Bar' },
		];
		if (showHeatmapVariant) {
			base.push({ value: 'heatmap', label: 'Heatmap' });
		}
		return base;
	}, [showHeatmapVariant]);

	useEffect(() => {
		if (!open) {
			return;
		}
		setRepositoryId(repositories[0]?.id ?? '');
		setTitle('');
		setWhere('');
		setIncludeArchived(true);
		setMetricSlug('');
		setXAxisSlug('');
		setXAxisTimeScale('');
		setVariant('line');
		setTargetAStr('');
		setTargetBStr('');
	}, [open, repositories]);

	useEffect(() => {
		if (variant === 'heatmap') {
			setXAxisTimeScale('');
			setTargetAStr('');
			setTargetBStr('');
		}
	}, [variant]);

	useEffect(() => {
		if (variant === 'heatmap' && !showHeatmapVariant) {
			setVariant('line');
		}
	}, [variant, showHeatmapVariant]);

	const handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		const trimmed = title.trim();
		const normalizedWhere = where.trim();
		const metricTrimmed = metricSlug.trim();
		const xAxisTrimmed = xAxisSlug.trim();
		if (!repositoryId || !trimmed || !metricTrimmed || !xAxisTrimmed) {
			return;
		}
		const variantOut = variant === 'heatmap' && !showHeatmapVariant ? 'line' : variant;
		const canUseTargets = variantOut === 'line' || variantOut === 'bar';
		onSubmit({
			repositoryId,
			title: trimmed,
			where: normalizedWhere || undefined,
			includeArchived,
			metric: metricTrimmed,
			xAxis: xAxisTrimmed,
			xAxisTimeScale: showTimeScaleControl && xAxisTimeScale ? xAxisTimeScale : null,
			variant: variantOut,
			targetA: canUseTargets ? parseOptionalChartTargetInput(targetAStr) : null,
			targetB: canUseTargets ? parseOptionalChartTargetInput(targetBStr) : null,
		});
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth scroll="body">
			<form onSubmit={handleSubmit} autoComplete="off">
				{elementSelectSlot != null ? (
					<Box display="flex" justifyContent="space-between" alignItems="center">
						<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>Create a chart</DialogTitle>
						<Box pr={2}>{elementSelectSlot}</Box>
					</Box>
				) : (
					<DialogTitle>Create a chart</DialogTitle>
				)}
				<Container>
					<Typography variant="body2" sx={{color: 'text.secondary'}}>
						Useful to for tracking key metrics over time.
					</Typography>
				</Container>
				<DialogContent>
					<Box display="flex" flexDirection="column" gap={2} mt={elementSelectSlot != null ? 0 : 1}>
						<TextField
							label="Title"
							value={title}
							onChange={(e) => setTitle(e.target.value)}
							fullWidth
							required
							autoFocus
							size="small"
							placeholder="Name this chart"
						/>
						<FormControl fullWidth required size="small">
							<InputLabel id="add-chart-repository-label">Collection</InputLabel>
							<Select
								labelId="add-chart-repository-label"
								label="Collection"
								value={repositoryId}
								onChange={(e) => {
									setRepositoryId(String(e.target.value));
									setMetricSlug('');
									setXAxisSlug('');
									setXAxisTimeScale('');
									setVariant('line');
									setTargetAStr('');
									setTargetBStr('');
									setWhere('');
									setIncludeArchived(true);
								}}
							>
								{repositories.map((r) => (
									<MenuItem key={r.id} value={r.id}>
										{r.title}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						{open && (
							<ChartWhereFilterBuilder
								chartId={repositoryId || '__none__'}
								initialWhere={where}
								onChange={setWhere}
								properties={propertyOptions}
								disabled={!repositoryId || isRepositoryFetching}
								includeArchived={includeArchived}
								onIncludeArchivedChange={setIncludeArchived}
								shouldAskAboutRelativeTime={true}
							/>
						)}
						<Typography variant="subtitle2">Presentation</Typography>
						<FormControl fullWidth required size="small" disabled={!repositoryId || isRepositoryFetching}>
							<InputLabel id="add-chart-metric-label">Metric</InputLabel>
							<Select
								labelId="add-chart-metric-label"
								label="Metric"
								required
								value={metricSlug}
								onChange={(e) => setMetricSlug(String(e.target.value))}
							>
								{isRepositoryFetching ? (
									<MenuItem value="" disabled>
										Loading properties...
									</MenuItem>
								) : (
									[{ slug: CHART_COUNT_METRIC_VALUE, label: 'Number of entries' }, ...metricOptions].map((option) => (
										<MenuItem key={option.slug} value={option.slug}>
											{option.label}
										</MenuItem>
									))
								)}
							</Select>
						</FormControl>
						<FormControl fullWidth required size="small" disabled={!repositoryId || isRepositoryFetching}>
							<InputLabel id="add-chart-x-axis-label">X-axis</InputLabel>
							<Select
								labelId="add-chart-x-axis-label"
								label="X-axis"
								required
								value={xAxisSlug}
								onChange={(e) => {
									setXAxisSlug(String(e.target.value));
									setXAxisTimeScale('');
									setTargetAStr('');
									setTargetBStr('');
								}}
							>
								{isRepositoryFetching ? (
									<MenuItem value="" disabled>
										Loading properties...
									</MenuItem>
								) : (
									xAxisOptions.map((option) => (
										<MenuItem key={option.slug} value={option.slug}>
											{option.label}
										</MenuItem>
									))
								)}
							</Select>
						</FormControl>
						{Boolean(xAxisSlug) && (
							<FormControl fullWidth size="small" disabled={!repositoryId || isRepositoryFetching}>
								<InputLabel id="add-chart-variant-label">Chart type</InputLabel>
								<Select
									labelId="add-chart-variant-label"
									label="Chart type"
									value={
										presentationVariantOptions.some((o) => o.value === variant)
											? variant
											: 'line'
									}
									onChange={(e) =>
										setVariant(String(e.target.value) as ChartPresentationVariant)
									}
								>
									{presentationVariantOptions.map((option) => (
										<MenuItem key={option.value} value={option.value}>
											{option.label}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}
						{showTimeScaleControl && (
							<FormControl fullWidth size="small" disabled={!repositoryId || isRepositoryFetching}>
								<InputLabel id="add-chart-time-scale-label">Time scale</InputLabel>
								<Select
									labelId="add-chart-time-scale-label"
									label="Time scale"
									value={xAxisTimeScale}
									onChange={(e) => setXAxisTimeScale(String(e.target.value) as 'D' | 'W' | 'M' | '')}
								>
									<MenuItem value="">None (default)</MenuItem>
									<MenuItem value="D">Daily</MenuItem>
									<MenuItem value="W">Weekly</MenuItem>
									<MenuItem value="M">Monthly</MenuItem>
								</Select>
							</FormControl>
						)}
						{showLineBarTargets && (
							<>
								<TextField
									label="Target A"
									value={targetAStr}
									onChange={(e) => setTargetAStr(e.target.value)}
									fullWidth
									size="small"
									type="text"
									inputMode="decimal"
									disabled={!repositoryId || isRepositoryFetching}
								/>
								<TextField
									label="Target B"
									value={targetBStr}
									onChange={(e) => setTargetBStr(e.target.value)}
									fullWidth
									size="small"
									type="text"
									inputMode="decimal"
									disabled={!repositoryId || isRepositoryFetching}
								/>
							</>
						)}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button type="button" onClick={onClose}>
						Cancel
					</Button>
					<Button
						type="submit"
						variant="contained"
						disabled={
							!repositoryId
							|| !title.trim()
							|| !metricSlug.trim()
							|| !xAxisSlug.trim()
						}
					>
						Add
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};

export default AddChartDialog;
